import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { IconClockExclamation } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useBranches } from '../branches/branchesApi';
import { useExpiring } from './inventoryApi';

export function ExpiringPage() {
  const { t, i18n } = useTranslation();
  const { data: branches, isLoading: branchesLoading } = useBranches();
  const [branchId, setBranchId] = useState<string | null>(null);
  const effectiveBranchId = branchId ?? branches?.[0]?.branchId ?? null;
  const [withinDays, setWithinDays] = useState<number>(14);

  const { data: rows, isLoading } = useExpiring(effectiveBranchId, withinDays);

  const branchOptions = useMemo(
    () =>
      (branches ?? []).map((b) => ({
        value: b.branchId,
        label: i18n.language === 'ar' ? b.nameAr : (b.nameEn ?? b.nameAr),
      })),
    [branches, i18n.language],
  );

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.operations')}
        title={t('expiring.page.title')}
        subtitle={t('expiring.page.subtitle')}
      />

      <Paper p="md">
        <Group gap="md" wrap="wrap">
          <Select
            label={t('expiring.page.branchLabel')}
            data={branchOptions}
            value={effectiveBranchId}
            onChange={setBranchId}
            searchable
            disabled={branchesLoading || branchOptions.length === 0}
            maw={320}
          />
          <NumberInput
            label={t('expiring.page.withinDaysLabel')}
            description={t('expiring.page.withinDaysHelp')}
            value={withinDays}
            onChange={(v) => setWithinDays(typeof v === 'number' ? v : 14)}
            min={0}
            max={365}
            step={1}
            maw={180}
          />
        </Group>
      </Paper>

      <Paper p={rows && rows.length > 0 ? 0 : 'lg'}>
        {!effectiveBranchId ? (
          <EmptyState
            icon={<IconClockExclamation size={22} stroke={1.6} />}
            title={t('expiring.empty.noBranchesTitle')}
            description={t('expiring.empty.noBranchesBody')}
          />
        ) : isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !rows || rows.length === 0 ? (
          <EmptyState
            icon={<IconClockExclamation size={22} stroke={1.6} />}
            title={t('expiring.empty.noneTitle')}
            description={t('expiring.empty.noneBody', { days: withinDays })}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('expiring.columns.item')}</Table.Th>
                <Table.Th>{t('expiring.columns.batch')}</Table.Th>
                <Table.Th>{t('expiring.columns.expiry')}</Table.Th>
                <Table.Th>{t('expiring.columns.daysLeft')}</Table.Th>
                <Table.Th>{t('expiring.columns.remaining')}</Table.Th>
                <Table.Th>{t('expiring.columns.quantity')}</Table.Th>
                <Table.Th>{t('expiring.columns.received')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.stockMovementId}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {i18n.language === 'ar' ? r.itemNameAr : (r.itemNameEn ?? r.itemNameAr)}
                    </Text>
                    <Text size="xs" c="dimmed" className="font-mono">
                      {r.itemSku}
                    </Text>
                  </Table.Td>
                  <Table.Td className="font-mono" fz="sm">
                    {r.batchNumber ?? '—'}
                  </Table.Td>
                  <Table.Td className="font-numeric">{r.expiryDate}</Table.Td>
                  <Table.Td>{renderDaysLeft(r.daysUntilExpiry, t)}</Table.Td>
                  <Table.Td className="font-numeric">
                    <Text fw={600} c="var(--wheels-ink-strong)">
                      {r.remainingQuantity}
                    </Text>
                  </Table.Td>
                  <Table.Td className="font-numeric" c="var(--wheels-ink-muted)" fz="xs">
                    {r.originalQuantity}
                  </Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)" fz="xs">
                    {dayjs(r.receivedAt).format('YYYY-MM-DD')}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

function renderDaysLeft(days: number, t: (k: string, v?: Record<string, unknown>) => string) {
  if (days < 0) {
    return (
      <Badge color="red" variant="filled" radius="sm" className="font-numeric">
        {t('expiring.badge.expired', { days: -days })}
      </Badge>
    );
  }
  if (days <= 3) {
    return (
      <Badge color="red" variant="light" radius="sm" className="font-numeric">
        {t('expiring.badge.daysLeft', { days })}
      </Badge>
    );
  }
  if (days <= 14) {
    return (
      <Badge color="orange" variant="light" radius="sm" className="font-numeric">
        {t('expiring.badge.daysLeft', { days })}
      </Badge>
    );
  }
  return (
    <Badge color="gray" variant="light" radius="sm" className="font-numeric">
      {t('expiring.badge.daysLeft', { days })}
    </Badge>
  );
}
